import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { store } from '../../store';
import { router } from '../../router';
import { t, setLocale } from '../../i18n';
import { showToast } from '../../components/ui/toast';
import '../../components/shared/auth-shell';
import type { SessionUser } from '../../store';

/** Student portal login — same framed shell as the admin login, portal copy. */
@customElement('cadence-portal-login-page')
export class CadencePortalLoginPage extends LitElement {
  @state() private email = '';
  @state() private password = '';
  @state() private loading = false;
  @state() private error = '';

  static styles = css`
    :host { display: block; }
    form { display: flex; flex-direction: column; gap: var(--space-4); }
    .form-error { color: var(--color-danger); font-size: 0.85rem; margin: 0; }
  `;

  private async submit() {
    if (!this.email || !this.password) { this.error = t('auth.login.required'); return; }
    this.loading = true; this.error = '';
    try {
      const { user, features, breakPeriod, school } = await api.post<{ user: SessionUser; features?: Record<string, boolean>; breakPeriod?: boolean; school?: { name: string; logo_url: string | null; default_language?: string } }>('/auth/login', { email: this.email, password: this.password });
      store.set({ user, features: features ?? {}, breakPeriod: breakPeriod === true, school: school ?? null });
      setLocale(user.language_preference || school?.default_language || 'en');
      store.applyTheme(user.dark_mode);
      router.navigate(user.user_type === 'student' ? '/portal/dashboard' : '/admin/dashboard');
    } catch (err) {
      const message = err instanceof ApiError && err.status === 401 ? t('auth.login.invalid') : (err as Error).message;
      this.error = message;
      showToast(message, 'error');
    } finally {
      this.loading = false;
    }
  }

  render() {
    return html`
      <cadence-auth-shell heading=${t('auth.portalLogin.title')} sub=${t('auth.portalLogin.subtitle')}>
        <form @submit=${(e: Event) => { e.preventDefault(); this.submit(); }}>
          ${this.error ? html`<p class="form-error" role="alert">${this.error}</p>` : ''}
          <cadence-input label=${t('auth.login.email')} type="email" autocomplete="username"
            .value=${this.email} @input=${(e: CustomEvent) => (this.email = e.detail)} @enter=${() => this.submit()}></cadence-input>
          <cadence-input label=${t('auth.login.password')} type="password" autocomplete="current-password"
            .value=${this.password} @input=${(e: CustomEvent) => (this.password = e.detail)} @enter=${() => this.submit()}></cadence-input>
          <cadence-button type="submit" full-width ?loading=${this.loading} @click=${() => this.submit()}>
            ${t('auth.portalLogin.submit')}
          </cadence-button>
        </form>
      </cadence-auth-shell>
    `;
  }
}
